import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Robot, X, PaperPlaneTilt, FileText, Sparkle, Buildings, ArrowSquareOut, Warning,
  Wrench, Plus, CaretDown,
} from "@phosphor-icons/react";

/**
 * Mini-asistente lateral que reutiliza el endpoint /repositorio/chat de la
 * aseguradora del expediente. Se invoca desde Review.jsx. Mantiene su propia
 * sesión por par (usuario, aseguradora) para que el historial sea persistente.
 */
export default function RepoCopilotDrawer({ open, onClose, expediente }) {
  const [aseg, setAseg] = useState(null);          // {id, name}
  const [aseguradoraStatus, setAseguradoraStatus] = useState("loading"); // loading|ok|not-found
  const [history, setHistory] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const endRef = useRef(null);

  // Resolver aseguradora del expediente → {id, name}
  useEffect(() => {
    if (!open || !expediente) return;
    const ed = expediente.extracted_data || {};
    const ref = (ed.aseguradora || expediente.detected_aseguradora || "").trim();
    if (!ref) { setAseguradoraStatus("not-found"); return; }
    let cancel = false;
    (async () => {
      setAseguradoraStatus("loading");
      try {
        const { data } = await api.get("/aseguradoras");
        const items = Array.isArray(data) ? data : (data.items || []);
        const norm = (s) => (s || "").toLowerCase().trim();
        const found = items.find(
          (a) => norm(a.name) === norm(ref)
            || norm(a.nombre_compacto) === norm(ref)
            || norm(a.code) === norm(ref),
        ) || items.find((a) => norm(a.name).includes(norm(ref)) || norm(ref).includes(norm(a.name)));
        if (cancel) return;
        if (!found) { setAseguradoraStatus("not-found"); return; }
        setAseg({ id: found.id, name: found.name });
        setAseguradoraStatus("ok");
      } catch (e) { if (!cancel) setAseguradoraStatus("not-found"); }
    })();
    return () => { cancel = true; };
  }, [open, expediente]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [history, busy]);

  // Sugerencias rápidas iniciales según el contexto del expediente
  const suggestions = (() => {
    const ed = expediente?.extracted_data || {};
    const causa = ed.causa || "";
    const ramo = ed.ramo || "";
    const out = [];
    if (causa) out.push(`¿Cómo procedo con un siniestro de ${causa.toLowerCase()}?`);
    if (ramo) out.push(`¿Qué baremo aplico para ${ramo.toLowerCase()}?`);
    out.push("¿Cuáles son las coberturas y franquicias estándar?");
    out.push("¿Qué circulares vigentes afectan a este tipo de siniestro?");
    return out.slice(0, 4);
  })();

  // Carga grupos de valoración del expediente para el selector "Añadir a..."
  const valoracionGrupos = expediente?.valoracion_grupos || [];

  // Stop-words típicas en preguntas de peritos (para mejorar matching del baremo)
  const STOP_WORDS = new Set([
    "como", "cómo", "que", "qué", "cual", "cuál", "cuanto", "cuánto",
    "cuando", "cuándo", "donde", "dónde", "porque", "porqué",
    "el", "la", "los", "las", "un", "una", "unos", "unas",
    "este", "esta", "estos", "estas", "ese", "esa", "esos", "esas",
    "del", "de", "a", "al", "en", "por", "para", "con", "sin",
    "sobre", "ante", "tras", "hacia", "hasta",
    "se", "me", "te", "le", "nos", "os", "les",
    "es", "son", "ser", "estar", "esta", "están",
    "y", "o", "u", "pero", "si", "no", "ya", "muy", "mas", "más",
    "quien", "quién", "tengo", "tiene", "hay", "puedo", "debo",
    "siniestro", "expediente", "expedientes", "aseguradora",
    "ocaso", "mapfre", "allianz", "generali", "mutua", "occident",
  ]);

  // Detección de cantidad + unidad en la pregunta del usuario
  // Ej. "20 m² de pintura", "5 ud", "3,5 horas" → {cantidad: 20, unidad: "m²"}
  const UNIDAD_NORMALIZA = {
    "m2": "m²", "m²": "m²", "metro cuadrado": "m²", "metros cuadrados": "m²",
    "m3": "m³", "m³": "m³", "metro cúbico": "m³", "metros cúbicos": "m³",
    "ml": "ml", "metro lineal": "ml", "metros lineales": "ml",
    "m": "ml", "metro": "ml", "metros": "ml",
    "ud": "ud", "uds": "ud", "unidad": "ud", "unidades": "ud", "pieza": "ud", "piezas": "ud",
    "h": "h", "hora": "h", "horas": "h",
    "kg": "kg", "kilo": "kg", "kilos": "kg",
    "t": "t", "tonelada": "t", "toneladas": "t",
  };

  const extractCantidadUnidad = (text) => {
    if (!text) return null;
    // Nota: NO usamos \b al final porque "²" no es carácter word y rompe la frontera.
    const re = /(\d+(?:[.,]\d+)?)\s*(m²|m2|m³|m3|ml|kg|h|t|metros\s+cuadrados|metros\s+cúbicos|metros\s+lineales|metros|metro|unidades|unidad|uds|ud|piezas|pieza|horas|hora|kilos|kilo|toneladas|tonelada)(?=\s|$|[^a-záéíóúñ])/i;
    const m = text.match(re);
    if (!m) return null;
    const cantidad = parseFloat(m[1].replace(",", "."));
    const unidadRaw = m[2].toLowerCase().replace(/\s+/g, " ").trim();
    const unidad = UNIDAD_NORMALIZA[unidadRaw] || unidadRaw;
    return Number.isFinite(cantidad) && cantidad > 0 ? { cantidad, unidad } : null;
  };

  // Busca partidas de baremo proactivamente a partir de la pregunta del usuario
  // Devuelve { items, qty } donde qty es {cantidad, unidad} detectada (o null).
  const fetchBaremoSuggestions = async (queryText) => {
    if (!aseg?.id) return { items: [], qty: null };
    const qty = extractCantidadUnidad(queryText);
    const tokens = (queryText || "")
      .toLowerCase()
      .replace(/[¿?¡!.,;:"()\[\]/]/g, " ")
      // Limpiar números (la cantidad la trataremos aparte)
      .replace(/\b\d+(?:[.,]\d+)?\b/g, " ")
      .split(/\s+/)
      .filter((t) => t.length >= 3 && !STOP_WORDS.has(t));
    if (tokens.length === 0) return { items: [], qty };
    const seen = new Set();
    const keywords = tokens.filter((t) => seen.has(t) ? false : (seen.add(t), true));
    const clean = keywords.slice(0, 8).join(" ");
    try {
      const { data } = await api.get(
        `/baremos-reparacion/autocomplete?aseguradora_id=${aseg.id}&q=${encodeURIComponent(clean)}&limit=3`,
      );
      return { items: data?.items || [], qty };
    } catch (e) { return { items: [], qty }; }
  };

  const insertarPartida = async (item, grupoId, cantidad = 1) => {
    if (!grupoId || !expediente?.id) return;
    try {
      const precio = item.precio_unitario
        || (Number(item.precio_mano_obra || 0) + Number(item.precio_material || 0))
        || 0;
      await api.post(`/expedientes/${expediente.id}/valoracion/grupos/${grupoId}/partidas`, {
        descripcion: item.descripcion,
        cantidad,
        precio_unitario: precio,
        notas: [item.codigo, item.capitulo, item.unidad ? `${item.unidad}` : null, item.baremo_name].filter(Boolean).join(" · "),
      });
      const subtotal = (cantidad * precio).toLocaleString("es-ES", { style: "currency", currency: "EUR" });
      toast.success(`Añadida "${item.descripcion.slice(0, 40)}" × ${cantidad} = ${subtotal}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al insertar partida");
    }
  };

  const send = async (overrideText) => {
    const msg = (overrideText ?? input).trim();
    if (!msg || !aseg?.id) return;
    setInput("");
    setHistory((h) => [...h, { role: "user", content: msg }]);
    setBusy(true);
    try {
      const [chatRes, baremoRes] = await Promise.all([
        api.post(`/aseguradoras/${aseg.id}/repositorio/chat`, {
          message: msg, session_id: sessionId || undefined,
        }),
        fetchBaremoSuggestions(msg),
      ]);
      const data = chatRes.data;
      if (!sessionId) setSessionId(data.session_id);
      setHistory((h) => [...h, {
        role: "assistant",
        content: data.answer,
        citations: data.citations,
        baremoSuggestions: baremoRes.items,
        detectedQty: baremoRes.qty,
      }]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error consultando al copiloto");
      setHistory((h) => [...h, { role: "assistant", content: "[Error]" }]);
    } finally { setBusy(false); }
  };

  if (!open) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/30 backdrop-blur-[2px] z-40" onClick={onClose} data-testid="repo-copilot-backdrop" />
      <aside
        className="fixed right-0 top-0 h-screen w-full sm:w-[460px] bg-white shadow-2xl z-50 flex flex-col border-l border-border"
        data-testid="repo-copilot-drawer"
      >
        <header className="px-4 py-3 border-b border-border bg-gradient-to-br from-amber-50 to-white">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-7 h-7 rounded-full bg-amber-600 text-white inline-flex items-center justify-center flex-shrink-0">
              <Robot className="w-4 h-4" weight="fill" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display font-bold text-[13px] tracking-tight">Copiloto del expediente</div>
              <div className="text-[10.5px] text-muted truncate">
                {aseg ? (
                  <span className="inline-flex items-center gap-1">
                    <Buildings className="w-3 h-3" weight="fill" />
                    Docs de <b>{aseg.name}</b>
                  </span>
                ) : "Identificando aseguradora…"}
              </div>
            </div>
            <button onClick={onClose} data-testid="repo-copilot-close" className="text-muted hover:text-foreground p-1">
              <X className="w-4 h-4" weight="bold" />
            </button>
          </div>
          {aseg && (
            <a
              href={`/repositorio/${aseg.id}`}
              target="_blank"
              rel="noreferrer"
              className="text-[10px] text-amber-700 hover:underline inline-flex items-center gap-1"
              data-testid="repo-copilot-open-full"
            >
              <ArrowSquareOut className="w-2.5 h-2.5" weight="bold" />
              Abrir repositorio completo
            </a>
          )}
        </header>

        <div className="flex-1 overflow-auto p-3 space-y-2">
          {aseguradoraStatus === "loading" && (
            <div className="text-center text-muted text-sm py-12">Identificando aseguradora…</div>
          )}
          {aseguradoraStatus === "not-found" && (
            <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-[12px] text-amber-900 flex items-start gap-2">
              <Warning className="w-4 h-4 flex-shrink-0 mt-0.5" weight="fill" />
              <div>
                <b>No he podido identificar la aseguradora</b> del expediente.
                Revisa que el campo "Aseguradora" esté completado en la pestaña Expediente.
              </div>
            </div>
          )}
          {aseguradoraStatus === "ok" && history.length === 0 && (
            <div className="space-y-3 py-4">
              <div className="text-center">
                <Sparkle className="w-9 h-9 text-amber-600 mx-auto mb-1" weight="fill" />
                <div className="text-[12px] text-muted px-2">
                  Pregúntame sobre las pólizas, circulares y procedimientos de
                  <b> {aseg.name}</b>. Te respondo citando los documentos del repositorio.
                </div>
              </div>
              <div className="space-y-1">
                <div className="text-[10px] uppercase tracking-widest font-bold text-muted px-1">Sugerencias</div>
                {suggestions.map((s, i) => (
                  <button
                    key={s}
                    onClick={() => send(s)}
                    data-testid={`repo-copilot-suggest-${i}`}
                    className="w-full text-left text-[12px] bg-white border border-border rounded px-2.5 py-1.5 hover:border-amber-400 hover:bg-amber-50/40 transition-colors"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
          {history.map((m, i) => (
            <div key={`${m.role}-${i}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[88%] rounded-lg px-3 py-2 text-[12.5px] ${m.role === "user" ? "bg-amber-600 text-white" : "bg-slate-50 border border-border text-foreground"}`} data-testid={`repo-copilot-msg-${i}`}>
                <div className="whitespace-pre-line leading-relaxed">{m.content}</div>
                {m.citations?.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-amber-200/40 space-y-0.5">
                    {m.citations.slice(0, 4).map((c, j) => (
                      <a
                        key={`${c.doc_title || "doc"}-${c.page_number || 0}-${j}`}
                        href={`/repositorio/${aseg.id}`}
                        target="_blank"
                        rel="noreferrer"
                        className="text-[10px] text-amber-800 hover:underline flex items-center gap-1 group"
                      >
                        <FileText className="w-2.5 h-2.5 flex-shrink-0" weight="fill" />
                        <span className="truncate">{c.doc_title}{c.page_number ? ` pág. ${c.page_number}` : ""}</span>
                      </a>
                    ))}
                  </div>
                )}
                {m.baremoSuggestions?.length > 0 && (
                  <BaremoSuggestionList
                    items={m.baremoSuggestions}
                    grupos={valoracionGrupos}
                    detectedQty={m.detectedQty}
                    onInsert={insertarPartida}
                  />
                )}
              </div>
            </div>
          ))}
          {busy && <div className="text-[11px] text-muted italic px-3">Pensando…</div>}
          <div ref={endRef}></div>
        </div>

        <form
          onSubmit={(e) => { e.preventDefault(); send(); }}
          className="px-3 py-2 border-t border-border flex gap-2"
        >
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={aseguradoraStatus === "ok" ? "Pregunta al copiloto…" : "—"}
            disabled={busy || aseguradoraStatus !== "ok"}
            data-testid="repo-copilot-input"
            className="flex-1 text-[12.5px]"
          />
          <button
            type="submit"
            disabled={busy || !input.trim() || aseguradoraStatus !== "ok"}
            data-testid="repo-copilot-send"
            className="btn-brand text-[12px] px-3 py-1.5 rounded-md font-semibold disabled:opacity-50 inline-flex items-center gap-1"
            title="Enviar"
          >
            <PaperPlaneTilt className="w-3.5 h-3.5" weight="fill" />
          </button>
        </form>
      </aside>
    </>
  );
}

const fmtEUR = (v) => v == null
  ? "—"
  : Number(v).toLocaleString("es-ES", { style: "currency", currency: "EUR" });

/**
 * Lista de partidas del baremo de reparación sugeridas tras una respuesta
 * del copiloto. Cada item se inserta en la valoración eligiendo grupo destino.
 * Si `detectedQty` está presente y su unidad coincide con la partida, ofrece
 * pre-rellenar la cantidad detectada.
 */
function BaremoSuggestionList({ items, grupos, detectedQty, onInsert }) {
  const [openIdx, setOpenIdx] = useState(null);
  const [pendingQty, setPendingQty] = useState({}); // {itemId: cantidad}

  // Por defecto, si hay cantidad detectada que coincide con la unidad, se pre-selecciona
  const initialQty = (item) => {
    if (!detectedQty) return 1;
    const u1 = (item.unidad || "").toLowerCase();
    const u2 = (detectedQty.unidad || "").toLowerCase();
    if (u1 && u2 && u1 === u2) return detectedQty.cantidad;
    return 1;
  };

  const cantidadFor = (item) => pendingQty[item.id] ?? initialQty(item);

  return (
    <div className="mt-2 pt-2 border-t border-amber-200/40 space-y-1.5" data-testid="repo-copilot-baremo-suggestions">
      <div className="text-[9.5px] uppercase tracking-widest font-bold text-amber-800 inline-flex items-center gap-1">
        <Wrench className="w-2.5 h-2.5" weight="fill" /> Partidas sugeridas del baremo
      </div>
      {detectedQty && (
        <div className="bg-amber-50 border border-amber-200 rounded p-1.5 text-[10.5px] text-amber-900" data-testid="copilot-detected-qty">
          He detectado <b>{detectedQty.cantidad} {detectedQty.unidad}</b> en tu pregunta.
          ¿Quieres añadir alguna de estas partidas a la valoración con esa cantidad?
        </div>
      )}
      {items.map((it, idx) => {
        const precio = it.precio_unitario
          ?? (Number(it.precio_mano_obra || 0) + Number(it.precio_material || 0));
        const cant = cantidadFor(it);
        const matchUnit = detectedQty
          && (it.unidad || "").toLowerCase() === (detectedQty.unidad || "").toLowerCase();
        const subtotal = cant && precio ? cant * precio : null;
        return (
          <div key={it.id} className="bg-white border border-amber-200 rounded p-1.5 text-[11px]" data-testid={`baremo-suggestion-${it.id}`}>
            <div className="flex items-center gap-1.5">
              {it.capitulo && <span className="text-[8.5px] bg-slate-100 text-slate-700 px-1 rounded font-bold">{it.capitulo}</span>}
              <span className="flex-1 min-w-0 truncate font-semibold">{it.descripcion}</span>
              <span className="font-mono font-bold text-amber-700">
                {fmtEUR(precio)}{it.unidad && <span className="text-muted text-[9px]">/{it.unidad}</span>}
              </span>
            </div>
            <div className="text-[9px] text-muted truncate mt-0.5">{it.baremo_name}</div>

            {/* Cantidad editable + subtotal */}
            <div className="mt-1.5 flex items-center gap-1.5">
              <label className="text-[10px] text-muted inline-flex items-center gap-1">
                Cantidad
                <input
                  type="number"
                  step="any"
                  min="0"
                  value={cant}
                  onChange={(e) => setPendingQty((p) => ({ ...p, [it.id]: parseFloat(e.target.value) || 0 }))}
                  data-testid={`baremo-suggestion-qty-${it.id}`}
                  className={`!text-[10.5px] !py-0.5 !px-1 w-16 font-mono ${matchUnit ? "!border-amber-400 !bg-amber-50" : ""}`}
                  title={matchUnit ? `Detectada en tu pregunta: ${detectedQty.cantidad} ${detectedQty.unidad}` : ""}
                />
              </label>
              {it.unidad && <span className="text-[9px] text-muted">{it.unidad}</span>}
              {subtotal != null && (
                <span className="text-[10.5px] font-bold text-amber-800 ml-auto">
                  = {fmtEUR(subtotal)}
                </span>
              )}
            </div>

            {/* Botón insertar */}
            <div className="mt-1.5 flex items-center gap-1">
              {grupos.length === 0 ? (
                <span className="text-[10px] text-muted italic">Crea un grupo en Valoración primero.</span>
              ) : grupos.length === 1 ? (
                <button
                  onClick={() => onInsert(it, grupos[0].id, cant)}
                  data-testid={`baremo-suggestion-insert-${it.id}`}
                  className="text-[10px] inline-flex items-center gap-1 px-2 py-0.5 rounded bg-amber-600 text-white hover:bg-amber-700 font-semibold"
                >
                  <Plus className="w-2.5 h-2.5" weight="bold" /> Añadir × {cant} a {grupos[0].nombre || grupos[0].interes || "valoración"}
                </button>
              ) : (
                <div className="relative">
                  <button
                    onClick={() => setOpenIdx(openIdx === idx ? null : idx)}
                    data-testid={`baremo-suggestion-pick-grupo-${it.id}`}
                    className="text-[10px] inline-flex items-center gap-1 px-2 py-0.5 rounded bg-amber-600 text-white hover:bg-amber-700 font-semibold"
                  >
                    <Plus className="w-2.5 h-2.5" weight="bold" /> Añadir × {cant} a…
                    <CaretDown className="w-2.5 h-2.5" weight="bold" />
                  </button>
                  {openIdx === idx && (
                    <div className="absolute left-0 top-full mt-1 z-10 bg-white border border-border rounded-md shadow-lg py-1 min-w-[200px]" data-testid={`baremo-suggestion-grupos-${it.id}`}>
                      {grupos.map((g) => (
                        <button
                          key={g.id}
                          onClick={() => { onInsert(it, g.id, cant); setOpenIdx(null); }}
                          className="w-full text-left text-[10.5px] px-2.5 py-1.5 hover:bg-amber-50 flex items-center gap-1.5"
                        >
                          <span className="text-[8.5px] uppercase text-muted">{g.tipo}</span>
                          <span className="font-semibold truncate">{g.nombre || g.interes || `Grupo ${g.id.slice(0, 6)}`}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

